import { User } from "firebase/auth";
import { useEffect, useMemo, useState } from "react";

import { fetchFormWithAuth, fetchJsonWithAuth } from "../api/client";
import { NoticeState } from "../components/ui";
import { defaultMeta, defaultSections } from "../shared/defaults";
import { buildCacheKey, getFreshCache, setCache } from "../shared/adminCache";
import {
  cloneJson,
  createClientId,
  fromHistoryMonthInput,
  isValidYearMonth,
  isValidYoutubeUrl,
  linesToList,
  toYoutubeEmbed,
} from "../shared/helpers";
import { AuditContext, SiteConfigMeta, SiteConfigResponse, SiteConfigSections, SiteConfigTabKey } from "../shared/siteContent";
import { useDraftPersistence } from "./useDraftPersistence";

export type SiteContentEditorTab = "youtube" | "popup" | "ir_ko" | "history";
type YoutubeCategory = "about" | "videos";

type YoutubeRow = {
  id: string;
  url: string;
};

type IrViewRow = {
  sourceIndex: number;
  item: SiteConfigSections["ir_ko"]["items"][number];
};

type MetaEditorState = {
  dirty: boolean;
  restored: boolean;
  partialFallback: boolean;
  lastTouchedAt: string;
  savedAt: string;
};

type DraftPayload = {
  tab: SiteContentEditorTab;
  sections: SiteConfigSections;
  youtubeRows: Record<YoutubeCategory, YoutubeRow[]>;
  metaState: MetaEditorState;
};

type UseSiteContentEditorOptions = {
  ready: boolean;
  user: User | null;
  getIdToken: (forceRefresh?: boolean) => Promise<string>;
};

const TAB_KEY = "adm.site_content.activeTab.react.v1";
const DRAFT_KEY = "adm.site_content.draft.react.v1";
const DRAFT_PREVIEW_KEY = "adm.site_content.draft.preview.react.v1";
const PAGE_SIZE = 5;
const ADMIN_CACHE_TTL_MS = 2 * 60 * 1000;

type SiteContentCacheData = {
  sections: SiteConfigSections;
  meta: SiteConfigMeta;
  auditContext: AuditContext | null;
};

// 편집/저장 이벤트 로그에 사용할 로컬 타임스탬프 문자열을 만든다.
function nowStamp() {
  const date = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

// site_config 응답을 기본값 보강 형태로 정규화해 편집 상태에 안전하게 주입한다.
function normalizeSiteConfigResponse(siteConfig: Partial<SiteConfigResponse> | null | undefined) {
  if (!siteConfig || typeof siteConfig !== "object") {
    return {
      sections: cloneJson(defaultSections),
      meta: defaultMeta,
      usedFallback: true,
    };
  }

  return {
    sections: cloneJson(siteConfig.sections || defaultSections),
    meta: siteConfig.meta || defaultMeta,
    usedFallback: false,
  };
}

// YouTube 섹션 배열 데이터를 UI 편집용 행(id+url) 구조로 변환한다.
function makeYoutubeRows(section: SiteConfigSections["youtube"]) {
  const aboutList = Array.isArray(section.about_us.urls) ? section.about_us.urls : [];
  const videosList = Array.isArray(section.videos.urls) ? section.videos.urls : [];
  // 비어있는 카테고리도 편집 가능하도록 최소 1행을 보장한다.
  const toRows = (list: string[]) => {
    const safe = list.length ? list : [""];
    return safe.map((url) => ({ id: createClientId("yt"), url: String(url || "") }));
  };
  return {
    about: toRows(aboutList),
    videos: toRows(videosList),
  };
}

// 편집 행 상태를 저장 API가 기대하는 youtube 문서 형태로 직렬화한다.
function toYoutubeSection(rows: Record<YoutubeCategory, YoutubeRow[]>) {
  // 공백 제거 + embed 정규화를 적용해 최종 URL 목록을 만든다.
  const normalize = (items: YoutubeRow[]) =>
    items
      .map((item) => String(item.url || "").trim())
      .filter(Boolean)
      .map((url) => toYoutubeEmbed(url));

  return {
    about_us: { urls: normalize(rows.about) },
    videos: { urls: normalize(rows.videos) },
  };
}

// 목록 길이를 기준으로 최소 1페이지 이상의 페이지 수를 계산한다.
function countPages(length: number) {
  return Math.max(1, Math.ceil(Math.max(0, length) / PAGE_SIZE));
}

// 입력 페이지를 [1, total] 범위로 보정한다.
function clampPage(page: number, total: number) {
  return Math.min(Math.max(1, Number(page || 1)), Math.max(1, total || 1));
}

/**
 * Site Content 편집기의 단일 상태 진입점.
 * draft 복원/저장, 검증, 저장 API 호출을 한곳에서 관리한다.
 */
export function useSiteContentEditor({ ready, user, getIdToken }: UseSiteContentEditorOptions) {
  const [tab, setTabState] = useState<SiteContentEditorTab>(() => {
    try {
      const saved = sessionStorage.getItem(TAB_KEY);
      if (saved === "youtube" || saved === "popup" || saved === "ir_ko" || saved === "history") return saved;
      return "youtube";
    } catch {
      return "youtube";
    }
  });
  const [sections, setSections] = useState<SiteConfigSections>(cloneJson(defaultSections));
  const [meta, setMeta] = useState<SiteConfigMeta>(defaultMeta);
  const [notice, setNotice] = useState<NoticeState>(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<SiteContentEditorTab | "all" | null>(null);
  const [metaState, setMetaState] = useState<MetaEditorState>({
    dirty: false,
    restored: false,
    partialFallback: false,
    lastTouchedAt: "",
    savedAt: "",
  });
  const [youtubeRows, setYoutubeRows] = useState<Record<YoutubeCategory, YoutubeRow[]>>(makeYoutubeRows(defaultSections.youtube));
  const [auditContext, setAuditContext] = useState<AuditContext | null>(null);
  const [popupFiles, setPopupFiles] = useState<Record<string, File | null>>({});
  const [irFiles, setIrFiles] = useState<Record<string, File | null>>({});
  const [youtubePage, setYoutubePage] = useState<Record<YoutubeCategory, number>>({ about: 1, videos: 1 });
  const [irPage, setIrPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPreviewPage, setHistoryPreviewPage] = useState(1);
  const [initialized, setInitialized] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const draftPersistence = useDraftPersistence<DraftPayload>({
    draftKey: DRAFT_KEY,
    previewKey: DRAFT_PREVIEW_KEY,
    buildPreview: (value) => ({
      ...value,
      metaState: {
        ...value.metaState,
        partialFallback: true,
      },
      sections: {
        ...value.sections,
        popup: value.sections.popup.map((popup) => ({
          ...popup,
          image_url: popup.image_url || null,
          image_storage_path: popup.image_storage_path || null,
        })),
      },
    }),
  });

  // 현재 탭을 상태/세션에 동기화해 새로고침 후에도 복원한다.
  function setTab(nextTab: SiteContentEditorTab) {
    setTabState(nextTab);
    try {
      sessionStorage.setItem(TAB_KEY, nextTab);
    } catch {
      // no-op
    }
  }

  // 사용자 입력 발생 시 dirty 상태와 마지막 변경 시각을 갱신한다.
  function markDirty() {
    setMetaState((prev) => ({
      ...prev,
      dirty: true,
      lastTouchedAt: nowStamp(),
    }));
  }

  // sections 원본과 파생 상태(YouTube 행/페이지네이션)를 동시에 초기화한다.
  function hydrateSections(nextSections: SiteConfigSections) {
    setSections(cloneJson(nextSections));
    setYoutubeRows(makeYoutubeRows(nextSections.youtube));
    setYoutubePage({ about: 1, videos: 1 });
    setIrPage(1);
    setHistoryPage(1);
    setHistoryPreviewPage(1);
  }

  // YouTube URL 형식 오류를 카테고리별로 수집한다.
  function validateYoutube() {
    const aboutInvalid = youtubeRows.about
      .map((row) => String(row.url || "").trim())
      .filter(Boolean)
      .filter((url) => !isValidYoutubeUrl(url));
    const videosInvalid = youtubeRows.videos
      .map((row) => String(row.url || "").trim())
      .filter(Boolean)
      .filter((url) => !isValidYoutubeUrl(url));
    return {
      aboutInvalid,
      videosInvalid,
      hasError: aboutInvalid.length > 0 || videosInvalid.length > 0,
    };
  }

  // Popup 저장 전 필수 조건(활성 시 이미지, 링크 URL 형식)을 검증한다.
  function validatePopup() {
    const errors: string[] = [];
    sections.popup.forEach((popup) => {
      if (popup.enabled && !(popup.image_url || popupFiles[popup.id])) {
        errors.push(`${popup.id}: enabled=true 일 때 이미지가 필요합니다.`);
      }
      const link = String(popup.link_url || "").trim();
      if (link && !/^https?:\/\//i.test(link)) {
        errors.push(`${popup.id}: 링크는 http/https 형식이어야 합니다.`);
      }
    });
    return errors;
  }

  // History 저장 전 연월 형식과 최소 1개 라인 존재를 검증한다.
  function validateHistory() {
    const errors: string[] = [];
    sections.history.forEach((item, index) => {
      if (!isValidYearMonth(item.year)) {
        errors.push(`History #${index + 1}: YYYY.MM 형식이 필요합니다.`);
      }
      const nonEmpty = (Array.isArray(item.contents) ? item.contents : []).map((line) => String(line || "").trim()).filter(Boolean);
      if (!nonEmpty.length) {
        errors.push(`History #${index + 1}: 최소 1개 이상의 내용을 입력해주세요.`);
      }
    });
    return errors;
  }

  // 저장 대상 탭(혹은 전체)에 맞는 검증 집합을 실행한다.
  function validateSection(target: SiteContentEditorTab | "all") {
    const warnings: string[] = [];
    if (target === "youtube" || target === "all") {
      const youtube = validateYoutube();
      if (youtube.hasError) {
        warnings.push("YouTube URL 형식 오류가 있습니다.");
      }
    }
    if (target === "popup" || target === "all") {
      warnings.push(...validatePopup());
    }
    if (target === "history" || target === "all") {
      warnings.push(...validateHistory());
    }
    return warnings;
  }

  // draft/cache/network 순으로 콘텐츠를 로드하고 동기화 상태를 관리한다.
  async function loadData(options?: { forceNetwork?: boolean }) {
    if (!ready || !user) return;
    const forceNetwork = Boolean(options?.forceNetwork);
    const cacheKey = buildCacheKey(user.uid, "site-content");
    let hasWarmData = false;
    setNotice(null);
    const restored = draftPersistence.loadDraft();
    if (restored.value) {
      hydrateSections(restored.value.sections);
      setTab(restored.value.tab);
      setMetaState({
        ...restored.value.metaState,
        dirty: true,
        restored: true,
        partialFallback: restored.usedPreview || restored.value.metaState.partialFallback,
      });
      if (restored.warning) {
        setNotice({ type: "warn", message: restored.warning });
      }
      hasWarmData = true;
    }

    if (!forceNetwork && !restored.value) {
      const cached = getFreshCache<SiteContentCacheData>(cacheKey, ADMIN_CACHE_TTL_MS);
      if (cached?.data) {
        hydrateSections(cached.data.sections);
        setMeta(cached.data.meta);
        setAuditContext(cached.data.auditContext || null);
        hasWarmData = true;
      }
    }

    setLoading(!hasWarmData);
    setSyncing(hasWarmData);
    if (hasWarmData) {
      setInitialized(true);
    }
    if (hasWarmData && !restored.warning) {
      setNotice({ type: "warn", message: "서버 최신 정보를 동기화 중입니다." });
    }

    try {
      const [siteConfig, auditInfo] = await Promise.all([
        fetchJsonWithAuth<SiteConfigResponse>("/api/admin/site-config", { method: "GET" }, getIdToken),
        fetchJsonWithAuth<AuditContext>("/api/admin/audit-context", { method: "GET" }, getIdToken),
      ]);
      const normalized = normalizeSiteConfigResponse(siteConfig);
      // draft가 있을 때는 사용자가 편집 중이던 맥락을 우선 보존한다.
      if (!restored.value && !metaState.dirty) {
        hydrateSections(normalized.sections);
      }
      setMeta(normalized.meta);
      const nextAuditContext = auditInfo && typeof auditInfo === "object" ? auditInfo : null;
      setAuditContext(nextAuditContext);
      setCache<SiteContentCacheData>(cacheKey, {
        sections: normalized.sections,
        meta: normalized.meta,
        auditContext: nextAuditContext,
      });

      if (normalized.usedFallback) {
        setNotice({ type: "warn", message: "site_config 응답이 비어 있어 기본값으로 표시 중입니다." });
      } else if (!restored.warning) {
        setNotice(null);
      }
    } catch (caught) {
      if (hasWarmData) {
        setNotice({ type: "warn", message: "캐시 데이터를 표시 중이며 최신 동기화에 실패했습니다." });
      } else {
        setNotice({ type: "error", message: caught instanceof Error ? caught.message : "콘텐츠 조회에 실패했습니다." });
      }
    } finally {
      setPopupFiles({});
      setIrFiles({});
      setLoading(false);
      setSyncing(false);
      setInitialized(true);
    }
  }

  useEffect(() => {
    void loadData({ forceNetwork: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, user?.uid]);

  useEffect(() => {
    if (!initialized || !metaState.dirty) return;
    // 변경 중에는 스냅샷을 로컬에 유지해 비정상 종료에 대비한다.
    const snapshot: DraftPayload = {
      tab,
      sections,
      youtubeRows,
      metaState,
    };
    const saved = draftPersistence.saveDraft(snapshot);
    if (saved.warning) {
      setNotice({ type: "warn", message: saved.warning });
    }
    setMetaState((prev) => {
      if (prev.partialFallback === saved.partial) return prev;
      return {
        ...prev,
        partialFallback: saved.partial,
      };
    });
  }, [draftPersistence, initialized, metaState, sections, tab, youtubeRows]);

  useEffect(() => {
    if (!metaState.dirty) return;

    // 저장 없이 이탈 시 draft를 초기화해 다음 진입 시 오래된 편집본이 남지 않게 한다.
    const clearDraftOnLeave = () => {
      try {
        // NOTE: "저장 없이 이탈" 정책에 따라 draft를 강제로 초기화한다.
        sessionStorage.removeItem(DRAFT_KEY);
        sessionStorage.removeItem(DRAFT_PREVIEW_KEY);
      } catch {
        // no-op
      }
    };

    window.addEventListener("beforeunload", clearDraftOnLeave);
    window.addEventListener("pagehide", clearDraftOnLeave);

    return () => {
      window.removeEventListener("beforeunload", clearDraftOnLeave);
      window.removeEventListener("pagehide", clearDraftOnLeave);
      // SPA 라우트 이동(언마운트)도 동일한 이탈 정책으로 처리한다.
      clearDraftOnLeave();
    };
  }, [metaState.dirty]);

  // target 탭(또는 전체)을 multipart 요청으로 저장하고 로컬 상태를 최신화한다.
  async function save(target: SiteContentEditorTab | "all") {
    const validationErrors = validateSection(target);
    if (validationErrors.length > 0) {
      setNotice({ type: "warn", message: validationErrors[0] });
      return false;
    }

    setSavingKey(target);
    setNotice(null);

    try {
      const formData = new FormData();

      if (target === "all") {
        formData.set(
          "payload",
          JSON.stringify({
            sections: {
              youtube: toYoutubeSection(youtubeRows),
              popup: sections.popup,
              ir_ko: sections.ir_ko,
              history: sections.history,
            },
          }),
        );
      } else {
        formData.set("targetKey", target);
        if (target === "youtube") formData.set("payload", JSON.stringify(toYoutubeSection(youtubeRows)));
        if (target === "popup") formData.set("payload", JSON.stringify(sections.popup));
        if (target === "ir_ko") formData.set("payload", JSON.stringify(sections.ir_ko));
        if (target === "history") formData.set("payload", JSON.stringify(sections.history));
      }

      sections.popup.forEach((popup) => {
        const file = popupFiles[popup.id];
        if (file) formData.append(`popupFile:${popup.id}`, file);
      });

      sections.ir_ko.items.forEach((item) => {
        const file = irFiles[item.id];
        if (file) formData.append(`irFile:${item.id}`, file);
      });

      const response = await fetchFormWithAuth<SiteConfigResponse>("/api/admin/site-config", formData, getIdToken, "PUT");
      const normalized = normalizeSiteConfigResponse(response);
      hydrateSections(normalized.sections);
      setMeta(normalized.meta);
      setPopupFiles({});
      setIrFiles({});
      if (user?.uid) {
        setCache<SiteContentCacheData>(buildCacheKey(user.uid, "site-content"), {
          sections: normalized.sections,
          meta: normalized.meta,
          auditContext,
        });
      }
      draftPersistence.clearDraft();
      setMetaState({
        dirty: false,
        restored: false,
        partialFallback: false,
        lastTouchedAt: nowStamp(),
        savedAt: nowStamp(),
      });
      setNotice({ type: "ok", message: target === "all" ? "전체 저장을 완료했습니다." : `${target} 저장을 완료했습니다.` });
      return true;
    } catch (caught) {
      setNotice({ type: "error", message: caught instanceof Error ? caught.message : "저장에 실패했습니다." });
      return false;
    } finally {
      setSavingKey(null);
    }
  }

  const youtubeValidation = useMemo(() => validateYoutube(), [youtubeRows]);

  const currentMetaText = useMemo(() => {
    const row = meta[tab] || defaultMeta.youtube;
    return `version ${row.version} · ${row.updated_by_display} · ${row.updated_at || "저장 기록 없음"}`;
  }, [meta, tab]);

  return {
    tab,
    setTab,
    sections,
    setSections,
    meta,
    notice,
    setNotice,
    loading,
    syncing,
    savingKey,
    metaState,
    currentMetaText,
    auditContext,
    popupFiles,
    setPopupFiles,
    irFiles,
    setIrFiles,
    youtubeRows,
    setYoutubeRows,
    youtubeValidation,
    youtubePage,
    setYoutubePage: (category: YoutubeCategory, page: number) => {
      const total = countPages((youtubeRows[category] || []).length);
      setYoutubePage((prev) => ({ ...prev, [category]: clampPage(page, total) }));
    },
    irPage,
    setIrPage: (page: number) => {
      setIrPage(clampPage(page, countPages(sections.ir_ko.items.length)));
    },
    historyPage,
    setHistoryPage: (page: number) => {
      setHistoryPage(clampPage(page, countPages(sections.history.length)));
    },
    historyPreviewPage,
    setHistoryPreviewPage,
    markDirty,
    reload: () => loadData({ forceNetwork: true }),
    saveSection: (target: SiteContentEditorTab) => save(target),
    saveAll: () => save("all"),
    updateYoutubeRow(category: YoutubeCategory, rowId: string, value: string) {
      setYoutubeRows((prev) => ({
        ...prev,
        [category]: prev[category].map((row) => (row.id === rowId ? { ...row, url: value } : row)),
      }));
      markDirty();
    },
    appendYoutubeRow(category: YoutubeCategory) {
      setYoutubeRows((prev) => {
        const nextRows = [{ id: createClientId("yt"), url: "" }, ...prev[category]];
        setYoutubePage((prevPage) => ({
          ...prevPage,
          [category]: 1,
        }));
        return {
          ...prev,
          [category]: nextRows,
        };
      });
      markDirty();
    },
    removeYoutubeRow(category: YoutubeCategory, rowId: string) {
      setYoutubeRows((prev) => {
        const next = prev[category].filter((row) => row.id !== rowId);
        return {
          ...prev,
          [category]: next.length ? next : [{ id: createClientId("yt"), url: "" }],
        };
      });
      markDirty();
    },
    reorderYoutubeRows(category: YoutubeCategory, fromIndex: number, toIndex: number) {
      setYoutubeRows((prev) => {
        const list = prev[category].slice();
        if (fromIndex < 0 || toIndex < 0 || fromIndex >= list.length || toIndex >= list.length || fromIndex === toIndex) {
          return prev;
        }
        const [moved] = list.splice(fromIndex, 1);
        list.splice(toIndex, 0, moved);
        return { ...prev, [category]: list };
      });
      markDirty();
    },
    updatePopup(slotIndex: number, key: "enabled" | "link_url", value: unknown) {
      setSections((prev) => {
        const next = cloneJson(prev);
        (next.popup[slotIndex] as Record<string, unknown>)[key] = value;
        return next;
      });
      markDirty();
    },
    updateIr(index: number, key: string, value: unknown) {
      setSections((prev) => {
        const next = cloneJson(prev);
        const current = next.ir_ko.items[index];
        if (!current) return prev;
        (current as Record<string, unknown>)[key] = value;
        if (index === 0) {
          if (key === "title") next.ir_ko.title = String(value || "");
          if (key === "published_at") next.ir_ko.published_at = String(value || "");
          if (key === "file_name") next.ir_ko.file_name = String(value || "");
          if (key === "file_url") next.ir_ko.file_url = value ? String(value) : null;
          if (key === "file_storage_path") next.ir_ko.file_storage_path = value ? String(value) : null;
        }
        return next;
      });
      markDirty();
    },
    addIrItem() {
      setSections((prev) => {
        const nextItem = {
          id: createClientId("ir"),
          name: "IR-",
          title: "",
          published_at: "",
          file_name: "",
          file_url: "",
          file_storage_path: "",
        };
        return {
          ...prev,
          ir_ko: {
            ...prev.ir_ko,
            items: [nextItem, ...prev.ir_ko.items],
          },
        };
      });
      setIrPage(1);
      markDirty();
    },
    removeIrItem(index: number) {
      setSections((prev) => {
        const next = cloneJson(prev);
        next.ir_ko.items = next.ir_ko.items.filter((_, currentIndex) => currentIndex !== index);
        if (!next.ir_ko.items.length) {
          next.ir_ko.items = cloneJson(defaultSections.ir_ko.items);
        }
        return next;
      });
      markDirty();
    },
    reorderIrItems(fromIndex: number, toIndex: number) {
      setSections((prev) => {
        const next = cloneJson(prev);
        const items = next.ir_ko.items;
        if (!items || fromIndex < 0 || toIndex < 0 || fromIndex >= items.length || toIndex >= items.length || fromIndex === toIndex) {
          return prev;
        }
        const [moved] = items.splice(fromIndex, 1);
        items.splice(toIndex, 0, moved);
        return next;
      });
      markDirty();
    },
    addHistoryItem() {
      const newItem = { id: createClientId("history"), year: "", contents: [""] };
      setSections((prev) => ({
        ...prev,
        history: [newItem, ...prev.history],
      }));
      setHistoryPage(1);
      markDirty();
    },
    removeHistoryItem(index: number) {
      setSections((prev) => {
        const next = cloneJson(prev);
        next.history = next.history.filter((_, currentIndex) => currentIndex !== index);
        if (!next.history.length) next.history = cloneJson(defaultSections.history);
        return next;
      });
      markDirty();
    },
    updateHistoryMonth(index: number, monthInput: string) {
      const yearMonth = fromHistoryMonthInput(monthInput);
      setSections((prev) => {
        const next = cloneJson(prev);
        if (!next.history[index]) return prev;
        next.history[index].year = yearMonth;
        return next;
      });
      markDirty();
    },
    updateHistoryLine(itemIndex: number, lineIndex: number, value: string) {
      setSections((prev) => {
        const next = cloneJson(prev);
        const item = next.history[itemIndex];
        if (!item) return prev;
        item.contents[lineIndex] = value;
        return next;
      });
      markDirty();
    },
    addHistoryLine(itemIndex: number) {
      setSections((prev) => {
        const next = cloneJson(prev);
        const item = next.history[itemIndex];
        if (!item) return prev;
        item.contents.push("");
        return next;
      });
      markDirty();
    },
    removeHistoryLine(itemIndex: number, lineIndex: number) {
      setSections((prev) => {
        const next = cloneJson(prev);
        const item = next.history[itemIndex];
        if (!item) return prev;
        item.contents = item.contents.filter((_, index) => index !== lineIndex);
        if (!item.contents.length) item.contents = [""];
        return next;
      });
      markDirty();
    },
    reorderHistoryCards(fromIndex: number, toIndex: number) {
      setSections((prev) => {
        const next = cloneJson(prev);
        if (fromIndex < 0 || toIndex < 0 || fromIndex >= next.history.length || toIndex >= next.history.length || fromIndex === toIndex) {
          return prev;
        }
        const [moved] = next.history.splice(fromIndex, 1);
        next.history.splice(toIndex, 0, moved);
        return next;
      });
      markDirty();
    },
    reorderHistoryLines(cardIndex: number, fromIndex: number, toIndex: number) {
      setSections((prev) => {
        const next = cloneJson(prev);
        const lines = next.history[cardIndex]?.contents;
        if (!lines || fromIndex < 0 || toIndex < 0 || fromIndex >= lines.length || toIndex >= lines.length || fromIndex === toIndex) {
          return prev;
        }
        const [moved] = lines.splice(fromIndex, 1);
        lines.splice(toIndex, 0, moved);
        return next;
      });
      markDirty();
    },
    saveIrPublishedAt(index: number) {
      const today = new Date();
      const pad = (num: number) => String(num).padStart(2, "0");
      const value = `${today.getFullYear()}.${pad(today.getMonth() + 1)}`;
      setSections((prev) => {
        const next = cloneJson(prev);
        if (!next.ir_ko.items[index]) return prev;
        next.ir_ko.items[index].published_at = value;
        return next;
      });
    },
    getHistoryPreviewItems() {
      return cloneJson(sections.history)
        .filter((item) => Array.isArray(item.contents))
        .sort((a, b) => String(b.year || "").localeCompare(String(a.year || "")));
    },
    getYoutubePreview(category: YoutubeCategory) {
      const list = youtubeRows[category]
        .map((row) => String(row.url || "").trim())
        .filter(Boolean)
        .map((url) => toYoutubeEmbed(url));
      return list;
    },
    getIrPreviewRows() {
      const rows: IrViewRow[] = cloneJson(sections.ir_ko.items)
        .map((item, sourceIndex) => ({ sourceIndex, item }));
      const totalPages = countPages(rows.length);
      const safePage = clampPage(irPage, totalPages);
      const start = (safePage - 1) * PAGE_SIZE;
      return {
        rows: rows.slice(start, start + PAGE_SIZE),
        totalPages,
        page: safePage,
        start,
      };
    },
    getHistoryRowsForPage() {
      const rows = cloneJson(sections.history);
      const totalPages = countPages(rows.length);
      const safePage = clampPage(historyPage, totalPages);
      const start = (safePage - 1) * PAGE_SIZE;
      return {
        rows: rows.slice(start, start + PAGE_SIZE),
        page: safePage,
        totalPages,
        start,
      };
    },
    getYoutubeRowsForPage(category: YoutubeCategory) {
      const rows = cloneJson(youtubeRows[category]);
      const totalPages = countPages(rows.length);
      const safePage = clampPage(youtubePage[category], totalPages);
      const start = (safePage - 1) * PAGE_SIZE;
      return {
        rows: rows.slice(start, start + PAGE_SIZE),
        page: safePage,
        totalPages,
        start,
      };
    },
    getYoutubeSummary(category: YoutubeCategory) {
      const list = youtubeRows[category].map((row) => String(row.url || "").trim()).filter(Boolean);
      const validCount = list.filter((url) => isValidYoutubeUrl(url)).length;
      return `${list.length}행 · 유효 URL ${validCount}개`;
    },
    historyLineText(lines: string[]) {
      return linesToList((Array.isArray(lines) ? lines : []).join("\n"));
    },
  };
}
